import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import { countryCapitals, defaultCapital } from "../data/capitals";
import { useTranslation, Trans } from "react-i18next";
import i18n from "../i18n"; 
import SeoHelmet from "../components/SeoHelmet";

const HomeMobile = () => {
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchDefaultEvents = async () => {
      try {
        const res = await fetch("https://ipapi.co/json/");
        const geo = await res.json();
        const countryCode = geo.country_code;
        const response = await api.get(`/api/events/public/by-country/${countryCode}`);
        setSuggestions(response.data);
        setShowSuggestions(true);
      } catch (error) {
        console.error("Erreur chargement événements par pays:", error);
      }
    };
    fetchDefaultEvents();
  }, []);

  const handleSearchChange = async (e) => {
    const value = e.target.value;
    setSearch(value);
    if (value.trim().length === 0) {
      setShowSuggestions(true);
      return;
    }
    try {
      const userLang = i18n.language.toUpperCase(); // ex: 'FR', 'DE', etc.
	  const url = `${process.env.REACT_APP_API_BASE_URL}/api/events/public/search?query=${encodeURIComponent(value)}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Erreur recherche événements");
      const data = await response.json();
      setSuggestions(data);
      setShowSuggestions(true);
    } catch (error) {
      console.error("Erreur recherche:", error);
    }
  };

  const handleSuggestionSelect = (event) => {
    navigate(`/event/user/${event.id_user}`);
  };

  function formatDateSafe(dateString, options) {
    try {
      return new Intl.DateTimeFormat(navigator.language || 'fr-FR', options).format(new Date(dateString));
    } catch (e) {
      console.error("Erreur formatage date:", e);
      return "";
    }
  }


  const handleFocus = () => {
    setShowSuggestions(true);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (inputRef.current && !inputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };

    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, []);

  return (
    <div className="bg-gray-100 min-h-screen px-4 py-8 flex flex-col justify-start items-center">
	  <SeoHelmet page="home" />
      <h1 className="text-5xl font-bold text-green-700 mb-4 text-center leading-tight">
        <Trans i18nKey="homeMobile.site_title" />
      </h1>
      <p className="text-center text-gray-600 text-lg mb-6">
        <Trans i18nKey="homeMobile.site_description" />
      </p>

      <div className="w-full relative" ref={inputRef}>
        <input
          type="text"
          value={search}
          onFocus={handleFocus}
          onChange={handleSearchChange}
          placeholder={t("homeMobile.search_placeholder")}
          className="w-full p-4 pl-10 border border-green-400 rounded-xl shadow focus:outline-none focus:ring-2 focus:ring-green-600"
        />
        <span className="absolute top-1/2 left-4 transform -translate-y-1/2 text-gray-400">🔍</span>

        {showSuggestions && suggestions.length > 0 && (
		<ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow max-h-64 overflow-y-auto mt-2">
		  {suggestions.slice(0, 500).map((event) => (
			<li
			  key={event.id}
			  onClick={() => handleSuggestionSelect(event)}
			  className="p-3 hover:bg-gray-100 cursor-pointer"
			>
			  <p className="font-semibold text-gray-800">{event.title}</p>

			  {/* Date et heure formatées */}
			  <p className="text-sm text-gray-500">
  			    {event.date
				  ? formatDateSafe(event.date, {
					  year: "numeric",
					  month: "long",
					  day: "numeric",
				    })
				  : event.start_date && event.end_date
				  ? `Du ${formatDateSafe(event.start_date, {
					  year: "numeric",
					  month: "long",
					  day: "numeric",
				    })} au ${formatDateSafe(event.end_date, {
  					year: "numeric",
					  month: "long",
					  day: "numeric",
				    })}`
				  : ""}
			    {event.time && event.date && (
  				<>
				    {" à "}
				    {formatDateSafe(`${event.date}T${event.time}`, {
  					hour: "2-digit",
					  minute: "2-digit",
					  hour12: false,
				    })}
				  </>
			    )}
			  </p>

			  {/* Lieu */}
			  <p className="text-sm text-gray-500">{event.location}</p>
			</li>
		  ))}
		</ul>
        )}
      </div>

      <div className="text-center mt-10">
        <p className="text-gray-700 text-lg mb-4 font-medium">
          <Trans i18nKey="homeMobile.site_description_suite" />
        </p>
        <button
          onClick={() => navigate("/event/create")}
          className="w-full px-6 py-3 bg-green-600 text-white text-lg rounded-xl shadow hover:bg-green-700 transition"
        >
          {t("homeMobile.create_event_button")}
        </button>
      </div>
    </div>
  );
};

export default HomeMobile;
